<script setup lang="ts">
import {MassCertificationCustomer} from "@/core/interfaces/MassCertification";
import {computed, ref, watch} from "vue";
import {CheckCheck} from "@lucide/vue";
import formatHelper from "@/core/helpers/formatHelper";

defineProps<{
    isLoading?: boolean;
    customers: MassCertificationCustomer[];
    mode: 'sms' | 'notification';
    total: number;
    page: number;
    limit: number;
}>();

const emits = defineEmits<{
    'update:options': [{page: number; itemsPerPage: number}];
    'update:selection': [MassCertificationCustomer[]];
}>();

const selected = ref<MassCertificationCustomer[]>([]);

watch(selected, (value) => {
    emits('update:selection', value);
});

const headers = computed(() => [
    {title: 'Patient', key: 'name', sortable: false},
    {title: 'Visites', key: 'visits', sortable: true},
    {title: 'Dernière visite', key: 'lastVisit', sortable: false},
    {title: 'Dernier SMS', key: 'messageDate', sortable: false},
]);
</script>

<template>
  <div>
    <v-data-table-server
      v-model="selected"
      :headers="headers"
      :items="customers"
      :items-length="total"
      :loading="isLoading"
      :page="page"
      :items-per-page="limit"
      item-value="id"
      :return-object="true"
      show-select
      select-strategy="page"
      :items-per-page-options="[10, 20, 50, 100]"
      color="primary"
      hover
      @update:options="emits('update:options', $event)"
    >
      <template #[`item.name`]="{ item }">
        <div class="flex items-center gap-3 py-2">
          <div class="flex flex-col">
            <span class="font-medium uppercase">
              {{ item.name }}
              <span v-if="item.birthDate" class="font-normal text-muted-foreground text-sm">
                {{ $filters.date(item.birthDate, 'relative', true) }}
              </span>
            </span>
            <span v-if="item.phone" class="text-muted-foreground text-sm">
              {{ formatHelper.readablePhone(item.phone) }}
            </span>
            <span v-else class="text-muted-foreground text-sm">Pas de numéro</span>
          </div>
        </div>
      </template>

      <template #[`item.visits`]="{ item }">
        <div class="flex flex-col gap-1 min-w-24">
          <span class="font-medium">
            {{ item.visits ?? 0 }}
            <span class="text-muted-foreground font-normal text-sm">{{
              $filters.pluralize('visite', item.visits ?? 0)
            }}</span>
          </span>
        </div>
      </template>

      <template #[`item.lastVisit`]="{ item }">
        <span v-if="item.lastVisit" class="text-sm">{{ $filters.date(item.lastVisit, 'short') }}</span>
        <span v-else class="text-muted-foreground text-sm">—</span>
      </template>

      <template #[`item.messageDate`]="{ item }">
        <span
          v-if="item.messageDate"
          class="gap-1 bg-secondary-dark/20 text-secondary-darkest text-sm font-medium px-2 py-0.5 rounded-full"
        >
          <CheckCheck class="size-4 inline-flex" />
          {{ $filters.date(item.messageDate, 'short') }}
        </span>
        <span v-else class="text-muted-foreground text-sm">Jamais contacté</span>
      </template>
    </v-data-table-server>
  </div>
</template>

<style scoped>
:deep(.v-data-table-header__content) {
    @apply uppercase text-muted-foreground;
}

:deep(.v-data-table__tr) {
    @apply border-2 border-transparent;
}

:deep(.v-data-table__tr:has(.v-checkbox-btn input:checked)) {
    @apply bg-primary/10 border-l-primary;
}
</style>
